import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useAllTimeSets } from "hooks/use-all-time-sets";
import { useCallback, useMemo } from "react";
import { Time } from "store/store-types";
import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";

const isWithinThreeHours = (timeA: IMoment, timeB: IMoment): boolean => {
    return Math.abs(timeA.diff(timeB, "minutes")) <= 180;
};

export const isOtherRuleTimeOverlapping = (
    time: TimeSlot,
    selectedTimes: Time[],
    allTimeSets: WebRule[],
    currentTimeSet: WebRule,
    ignoredRuleIds?: Set<number>
) => {
    return selectedTimes?.some((selectedTime) => {
        if (selectedTime.ruleId === time.ruleId) return false;
        if (selectedTime.ruleId !== null && ignoredRuleIds?.has(selectedTime.ruleId)) return false;

        const selectedTimeRule = allTimeSets.find((rule) => rule.id === selectedTime.ruleId);
        if (!selectedTimeRule) return false;

        const currentTimeEnd = time.end?.clone().subtract(currentTimeSet?.recoupTime, "minutes");
        const selectedTimeEnd = selectedTime.end
            ?.clone()
            .subtract(selectedTimeRule.recoupTime, "minutes");

        return selectedTime.start.isBefore(currentTimeEnd) && time.start.isBefore(selectedTimeEnd);
    });
};

// Times to nudge:
// Based on each selected time (selectedTimesExcludingOpenSection), we want to identify the first available timeslot in the provided time set that follows or precedes the selected time.
// This timeslot should not overlap with previously selected times and must be within 2 hours of the selected time—either up to 2 hours before time.start or up to 2 hours after time.end.
// The closest available option in each direction should be chosen.

export const useTimesToNudge = (
    sectionIds: number[],
    ignoredRuleIds?: Set<number>,
    chainConflictStartTimes?: Set<string>
): Map<number, Time[]> => {
    const allTimeSets = useAllTimeSets();
    const ruleIds = allTimeSets?.map((timeSet) => timeSet.id);
    const selectedTimes = useWebBooking().data?.getBookTimes();

    const selectedTimesExcludingOpenSection = selectedTimes?.filter(
        (time) =>
            !sectionIds.includes(time.sectionId ?? 0) &&
            !(time.ruleId !== null && ignoredRuleIds?.has(time.ruleId))
    );

    const getValidTimes = useCallback(
        (
            times: TimeSlot[],
            selectedTime: Time,
            selectedTimeRule: WebRule,
            currentTimeSet: WebRule,
            direction: "after" | "before"
        ): Time[] => {
            const timeBoundary =
                direction === "after" ? selectedTime.end?.clone() : selectedTime.start.clone();
            const comparisonTime = direction === "after" ? selectedTime.start : selectedTime.end;

            if (!timeBoundary || !comparisonTime) return [];

            return times.filter((time) => {
                const boundaryCondition =
                    direction === "after"
                        ? time.start.isSameOrAfter(
                              timeBoundary.subtract(selectedTimeRule.recoupTime, "minutes")
                          )
                        : time.start.isBefore(
                              timeBoundary.subtract(selectedTimeRule.recoupTime, "minutes")
                          );
                return (
                    boundaryCondition &&
                    // Note: ignoredRuleIds is not passed here — sibling articles are
                    // already excluded from selectedTimesExcludingOpenSection above.
                    // Chain conflicts are handled by filtering possibleTimes results.
                    !isOtherRuleTimeOverlapping(
                        time,
                        selectedTimesExcludingOpenSection ?? [],
                        allTimeSets ?? [],
                        currentTimeSet
                    ) &&
                    isWithinThreeHours(
                        direction === "after" ? time.start : time.end,
                        comparisonTime
                    )
                );
            });
        },
        [allTimeSets, selectedTimesExcludingOpenSection]
    );

    const timesToNudgeMap = useMemo(() => {
        const timesToNudgeMap = new Map<number, Time[]>();
        const firstIndex = 0;

        ruleIds?.forEach((ruleId) => {
            const currentTimeSet = allTimeSets?.find((rule) => rule.id === ruleId);
            if (!currentTimeSet) return;

            const ruleTimesToNudge: Time[] = [];
            selectedTimesExcludingOpenSection?.forEach((selectedTime) => {
                const selectedTimeRule = allTimeSets?.find(
                    (rule) => rule.id === selectedTime.ruleId
                );
                if (!selectedTimeRule) return;

                const possibleTimesAfter = getValidTimes(
                    currentTimeSet.times,
                    selectedTime,
                    selectedTimeRule,
                    currentTimeSet,
                    "after"
                ).filter((t) => !chainConflictStartTimes?.has(t.start.format("HH:mm")));
                const followingTime = possibleTimesAfter.at(firstIndex);
                if (followingTime) {
                    ruleTimesToNudge.push(followingTime);
                }

                const possibleTimesBefore = getValidTimes(
                    currentTimeSet.times,
                    selectedTime,
                    selectedTimeRule,
                    currentTimeSet,
                    "before"
                ).filter((t) => !chainConflictStartTimes?.has(t.start.format("HH:mm")));
                const previousTime = possibleTimesBefore.at(-1);
                if (previousTime) {
                    ruleTimesToNudge.push(previousTime);
                }
            });

            timesToNudgeMap.set(ruleId, ruleTimesToNudge);
        });

        return timesToNudgeMap;
    }, [
        ruleIds,
        allTimeSets,
        selectedTimesExcludingOpenSection,
        getValidTimes,
        chainConflictStartTimes,
    ]);

    return timesToNudgeMap;
};
